import { getAPI, postAPI } from "./Networking";
import { SwayyAPIResponse } from "../../../models/NetworkResponse";
import {
  CreateSwayyCreditNotePayload,
  GetSwayyInvoicesParams,
  SwayyCreditNote,
  SwayyInvoice,
  SwayyInvoicesListData,
} from "../../../models/SwayyInvoice/SwayyInvoice";

const getErrorMessage = (error: unknown, fallback: string): string => {
  const axiosMessage = (error as any)?.response?.data?.message;
  if (typeof axiosMessage === "string" && axiosMessage.trim()) {
    return axiosMessage;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
};

export default class SwayyInvoiceAPI {
  getInvoices = async (
    params: GetSwayyInvoicesParams,
  ): Promise<SwayyInvoicesListData> => {
    try {
      const query: Record<string, string | number> = {
        page: params.page,
        limit: params.limit,
      };
      if (params.order_id) query.order_id = params.order_id;
      if (params.shipment_id) query.shipment_id = params.shipment_id;
      if (params.status) query.status = params.status;
      if (params.type) query.type = params.type;
      if (params.q) query.q = params.q;

      const response = (await getAPI(
        "/v1/invoices/admin",
        query,
      )) as SwayyAPIResponse<SwayyInvoicesListData>;

      const payload = response.data ?? response.result;
      if (!response.success || !payload) {
        throw new Error(response.message || "Failed to fetch invoices");
      }

      return {
        invoices: payload.invoices ?? [],
        pagination: {
          total: payload.pagination?.total ?? 0,
          page: payload.pagination?.page ?? params.page,
          limit: payload.pagination?.limit ?? params.limit,
        },
      };
    } catch (error) {
      throw new Error(getErrorMessage(error, "Failed to fetch invoices"));
    }
  };

  getOrderInvoices = async (
    orderId: number | string,
  ): Promise<SwayyInvoice[]> => {
    try {
      const response = (await getAPI(
        `/v1/orders/admin/${orderId}/invoices`,
      )) as SwayyAPIResponse<
        SwayyInvoice[] | { invoices: SwayyInvoice[]; data?: SwayyInvoice[] }
      >;

      const payload = response.data ?? response.result;
      if (!response.success || !payload) {
        throw new Error(response.message || "Failed to fetch invoices");
      }

      if (Array.isArray(payload)) return payload;
      if (Array.isArray(payload.invoices)) return payload.invoices;
      if (Array.isArray(payload.data)) return payload.data;
      return [];
    } catch (error) {
      throw new Error(getErrorMessage(error, "Failed to fetch invoices"));
    }
  };

  regenerateInvoice = async (
    invoiceId: number | string,
  ): Promise<{ invoice_id: number; status: string }> => {
    try {
      const response = (await postAPI(
        `/v1/invoices/admin/${invoiceId}/regenerate`,
      )) as SwayyAPIResponse<{ invoice_id: number; status: string }>;

      const payload = response.data ?? response.result;
      if (!response.success || !payload?.invoice_id) {
        throw new Error(response.message || "Failed to regenerate invoice");
      }

      return payload;
    } catch (error) {
      throw new Error(getErrorMessage(error, "Failed to regenerate invoice"));
    }
  };

  getInvoiceCreditNotes = async (
    invoiceId: number | string,
  ): Promise<SwayyCreditNote[]> => {
    try {
      const response = (await getAPI(
        `/v1/invoices/admin/${invoiceId}/credit-notes`,
      )) as SwayyAPIResponse<{ credit_notes: SwayyCreditNote[] }>;

      const payload = response.data ?? response.result;
      if (!response.success || !payload) {
        throw new Error(response.message || "Failed to fetch credit notes");
      }

      return payload.credit_notes ?? [];
    } catch (error) {
      throw new Error(getErrorMessage(error, "Failed to fetch credit notes"));
    }
  };

  createCreditNote = async (
    invoiceId: number | string,
    body: CreateSwayyCreditNotePayload,
  ): Promise<{ credit_note_id: number }> => {
    try {
      const response = (await postAPI(
        `/v1/invoices/admin/${invoiceId}/credit-notes`,
        body,
      )) as SwayyAPIResponse<{ credit_note_id: number }>;

      const payload = response.data ?? response.result;
      if (!response.success || !payload?.credit_note_id) {
        throw new Error(response.message || "Failed to create credit note");
      }

      return payload;
    } catch (error) {
      throw new Error(getErrorMessage(error, "Failed to create credit note"));
    }
  };

}
